import type { NotificationChannel, NotificationType } from "./schema";

export type UserAudience = "employee" | "employer";

export type NotificationCategory =
  | "messages"
  | "applications"
  | "interviews"
  | "account";

export type NotificationDefinition = {
  type: NotificationType;
  label: string;
  description: string;
  category: NotificationCategory;
  order: number;
  channels: readonly NotificationChannel[];
  audience: UserAudience[];
};

const _DEFINITIONS = [
  {
    type: "message.new",
    label: "New messages",
    description: "Messages sent to your VacWork inbox.",
    category: "messages",
    order: 1,
    channels: ["in_app", "email"],
    audience: ["employee", "employer"],
  },
  {
    type: "application.submitted",
    label: "Application submitted",
    description: "Confirmation when your application is submitted.",
    category: "applications",
    order: 1,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "application.new",
    label: "New applications",
    description: "Candidates applying to your job listings.",
    category: "applications",
    order: 1,
    channels: ["in_app", "email"],
    audience: ["employer"],
  },
  {
    type: "application.daily_digest",
    label: "Daily application digest",
    description: "A daily summary of applications awaiting review.",
    category: "applications",
    order: 2,
    channels: ["email"],
    audience: ["employer"],
  },
  {
    type: "application.draft_reminder",
    label: "Draft application reminders",
    description: "Reminders to finish applications you have started.",
    category: "applications",
    order: 2,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "job.nearby_recommendations",
    label: "Nearby job recommendations",
    description: "A weekly selection of open jobs near your saved location.",
    category: "applications",
    order: 3,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "application.shortlisted",
    label: "Application shortlisted",
    description: "Your application is shortlisted by an employer.",
    category: "applications",
    order: 4,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "application.interviewing",
    label: "Application moved to interviewing",
    description: "Your application is moved to the interviewing stage.",
    category: "applications",
    order: 5,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "application.accepted",
    label: "Application accepted",
    description: "Your application is accepted by an employer.",
    category: "applications",
    order: 6,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "application.rejected",
    label: "Application rejected",
    description: "Your application is not selected for a job listing.",
    category: "applications",
    order: 7,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "interview.invite",
    label: "Interview invites",
    description: "Employers inviting you to interviews.",
    category: "interviews",
    order: 1,
    channels: ["in_app", "email"],
    audience: ["employee"],
  },
  {
    type: "interview.status_changed",
    label: "Interview status updates",
    description: "Candidates responding to interview invites.",
    category: "interviews",
    order: 2,
    channels: ["in_app", "email"],
    audience: ["employer"],
  },
  {
    type: "interview.reminder",
    label: "Interview reminders",
    description: "Reminders before interviews and nudges for pending replies.",
    category: "interviews",
    order: 3,
    channels: ["in_app", "email"],
    audience: ["employee", "employer"],
  },
  {
    type: "conversation.ended",
    label: "Conversation ended",
    description: "When an employer closes a conversation.",
    category: "messages",
    order: 2,
    channels: ["in_app"],
    audience: ["employee"],
  },
  {
    type: "user.onboarding_completion_reminder",
    label: "Onboarding completion reminders",
    description: "Reminders to finish setting up your VacWork account.",
    category: "account",
    order: 1,
    channels: ["email"],
    audience: ["employee", "employer"],
  },
] as const satisfies readonly NotificationDefinition[];

export const NOTIFICATION_DEFINITIONS: readonly NotificationDefinition[] =
  _DEFINITIONS;

export const ORGANIZATION_SCOPED_NOTIFICATION_TYPES = [
  "message.new",
  "application.submitted",
  "application.new",
  "application.shortlisted",
  "application.interviewing",
  "application.accepted",
  "application.rejected",
  "interview.invite",
  "interview.status_changed",
  "interview.reminder",
  "conversation.ended",
] as const satisfies readonly NotificationType[];

export function getNotificationDefinition(type: NotificationType) {
  return NOTIFICATION_DEFINITIONS.find(
    (definition) => definition.type === type
  );
}

export function supportsNotificationChannel(
  type: NotificationType,
  channel: NotificationChannel
) {
  return getNotificationDefinition(type)?.channels.includes(channel) ?? false;
}

export function isOrganizationScopedNotification(type: NotificationType) {
  return ORGANIZATION_SCOPED_NOTIFICATION_TYPES.some((value) => value === type);
}
